import React, { useState, useEffect } from 'react';
import { Grid3X3, Send, Loader, AlertCircle, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getProjectData, saveProjectData, getGlobalData } from '../utils/projectStorage';
import './JobGrids.css';

interface JobGridsData {
  settings?: {
    generalInstructions: string;
    sampleJobDescription: string;
  };
  ingestedContent?: string;
  messageHistory?: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
  responses?: Array<{
    id: string;
    question: string;
    response: string;
    timestamp: Date;
  }>;
}

const motivationalDimensions = [
  'Achiever', 'Driver', 'Influencer', 'Learner',
  'Optimizer', 'Orchestrator', 'Relator', 'Visionary'
];

const JobGrids: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [selectedDimension, setSelectedDimension] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState('');
  const [projectData, setProjectData] = useState<JobGridsData>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    // Load project data
    const data = getProjectData('job-grids');
    setProjectData(data);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    const globalData = getGlobalData();
    if (!globalData.apiKey) {
      setError('Please configure your Anthropic API key in settings.');
      return;
    }

    // Check if settings have been processed
    const ingestedContent = projectData.ingestedContent;
    if (!ingestedContent) {
      setError('Please configure and process settings first in the Settings tab.');
      return;
    }

    setIsLoading(true);
    setError('');
    setResponse('');

    try {
      const Anthropic = (await import('@anthropic-ai/sdk')).default;
      const anthropic = new Anthropic({
        apiKey: globalData.apiKey,
        dangerouslyAllowBrowser: true
      });

      const prompt = `Job Description:
${question}`;

      // Get project-specific message history for context
      let previousMessages: any[] = [];
      
      if (projectData.messageHistory) {
        try {
          // Include relevant previous messages for context
          previousMessages = projectData.messageHistory.slice(-10).map((msg: any) => ({
            role: msg.role,
            content: msg.content
          }));
        } catch (error) {
          console.error('Error parsing message history:', error);
        }
      }

      const response = await anthropic.messages.create({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 4096,
        system: [
          {
            type: 'text',
            text: ingestedContent,
            cache_control: { type: 'ephemeral' }
          }
        ],
        messages: [
          ...previousMessages,
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const responseText = response.content[0].type === 'text' ? response.content[0].text : '';
      setResponse(responseText);

      // Save response to project data
      const newResponse = {
        id: Date.now().toString(),
        question,
        response: responseText,
        timestamp: new Date()
      };
      
      const updatedData = {
        ...projectData,
        responses: [...(projectData.responses || []), newResponse]
      };
      
      setProjectData(updatedData);
      saveProjectData('job-grids', updatedData);

      // Update project-specific message history
      const updatedHistory = [
        ...(projectData.messageHistory || []),
        {
          id: Date.now().toString(),
          role: 'user' as const,
          content: `[Job Grids - ${question}`,
          timestamp: new Date()
        },
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant' as const,
          content: responseText,
          timestamp: new Date()
        }
      ];
      
      updatedData.messageHistory = updatedHistory;
      
    } catch (err: any) {
      console.error('Error calling Claude API:', err);
      setError(err.message || 'An error occurred while processing your request');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="job-grids">
      <div className="job-grids-content">
        <form onSubmit={handleSubmit} className="question-form">
          <div className="form-group">
            <label htmlFor="question">Job Grid</label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder=""
              rows={4}
              required
            />
          </div>

          <button type="submit" disabled={isLoading || !question.trim()}>
            {isLoading ? (
              <>
                <Loader className="spinner" size={16} />
                Analyzing...
              </>
            ) : (
              <>
                <Send size={16} />
                Generate Job Grid
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {response && (
          <div className="response-section">
            <div className="response-header">
              <h3>Job Grid Analysis</h3>
              <button
                onClick={() => copyToClipboard(response, 'current')}
                className="copy-button"
                title="Copy response"
              >
                {copiedId === 'current' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <div className="response-content">
              <ReactMarkdown>{response}</ReactMarkdown>
            </div>
          </div>
        )}

        {projectData.responses && projectData.responses.length > 0 && (
          <div className="previous-responses">
            <h3>Previous Job Grid Analyses</h3>
            {projectData.responses.slice().reverse().map((resp) => (
              <div key={resp.id} className="response-item">
                <div className="response-meta">
                  <span className="timestamp">{new Date(resp.timestamp).toLocaleString()}</span>
                  <button
                    onClick={() => copyToClipboard(resp.response, resp.id)}
                    className="copy-button"
                    title="Copy response"
                  >
                    {copiedId === resp.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <div className="question-text">
                  <strong>Q:</strong> {resp.question}
                </div>
                <div className="response-content">
                  <ReactMarkdown>{resp.response}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobGrids;